import { useCallback, useRef } from 'react';
import type { CallerProvidedTagsT, PerformanceMetricActionT } from '../../analytics/types/performance';
import { DEFAULT_PAINT_LATENCY_THROTTLE_MS } from '../../constants/performance';
import { usePerformanceMetrics } from './use-performance-metrics';

/** Check if Performance User Timing API is available (not in JSDOM/test environments) */
const isPerformanceTimingAvailable = typeof performance !== 'undefined' && typeof performance.mark === 'function' && typeof performance.measure === 'function' && typeof performance.now === 'function';
type UseMeasurePaintLatencyOptions = {
  /** Minimum interval between consecutive measurements (ms). Default: 200ms. Set to 0 to disable. */
  throttleMs?: number;
  /** Minimum duration to emit (ms). Default: 0. Set to 16 to skip sub-frame measurements. */
  minDurationMs?: number;
};

/**
 * Measures dispatch-to-paint latency via performance.mark + double-rAF.
 * Double-rAF: first fires before paint, second fires after — capturing total user-perceived latency.
 */
export function useMeasurePaintLatency(options?: UseMeasurePaintLatencyOptions) {
  const throttleMs = options?.throttleMs ?? DEFAULT_PAINT_LATENCY_THROTTLE_MS;
  const minDurationMs = options?.minDurationMs ?? 0;
  const {
    captureLatency
  } = usePerformanceMetrics();
  const captureLatencyRef = useRef(captureLatency);
  captureLatencyRef.current = captureLatency;
  const pendingRef = useRef(false);
  const lastEmitTimeRef = useRef(-Infinity);
  return useCallback((metricName: PerformanceMetricActionT, dispatchFn: () => void, tags?: CallerProvidedTagsT) => {
    if (!isPerformanceTimingAvailable) {
      dispatchFn();
      return;
    }
    const isAnyPending = pendingRef.current;
    const shouldThrottle = throttleMs > 0;
    const isThrottleExpired = performance.now() - lastEmitTimeRef.current >= throttleMs;
    let shouldMeasure = true;
    if (isAnyPending) {
      shouldMeasure = false;
    } else if (shouldThrottle && !isThrottleExpired) {
      shouldMeasure = false;
    }
    const markName = metricName + '_start';
    if (shouldMeasure) {
      pendingRef.current = true;
      performance.mark(markName);
    }
    dispatchFn();
    if (shouldMeasure) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const measure = performance.measure(metricName, markName);
            if (measure.duration >= minDurationMs) {
              captureLatencyRef.current({
                name: metricName,
                durationMs: measure.duration,
                tags
              });
            }
          } catch {
            // Silently handle if marks were cleared
          } finally {
            performance.clearMarks(markName);
            performance.clearMeasures(metricName);
            pendingRef.current = false;
            lastEmitTimeRef.current = performance.now();
          }
        });
      });
    }
  }, [throttleMs, minDurationMs]);
}